/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import moment from 'moment';

import { CardClips, CardEnrollmentDate, CardWatchNow, Grid } from '@coursera/cds-core';
import type { ProductCardProps } from '@coursera/cds-core';

import getDurationFormatted from 'bundles/enterprise-legacy-learner-home/utils/getDurationFormatted';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import {
  getImagePropOverrides,
  getPartnerNamesAndLogos,
} from 'bundles/product-card/components/utils/productCardV2Utils';
import { getProductCardStatusTags } from 'bundles/product-card/utils/getProductCardStatusTags';
import { LEARNING_PRODUCTS_ENUM_MAP, getTranslatedDifficulty } from 'bundles/search-common/constants';
import type { PRODUCT_DIFFICULTY_LEVELS } from 'bundles/search-common/constants';
import { isShortFormContent } from 'bundles/search-common/utils/SearchUtils';
import { Formatters } from 'bundles/teach-course-shared/lib/numberRenders';

import _t from 'i18n!nls/enterprise-learner-search';

const styles = {
  targetSearchCardWrapper: css`
    height: 100%;
    padding: 0 6px 24px;

    > * {
      height: inherit;
    }
    @media (max-width: 599.95px) {
      padding: 0 0 24px 0;
    }
  `,
};

const LAZY_LOAD_THRESHOLD = 5;

const getVideoMetadataFromHit = (videoData: { videosInLesson?: number; duration?: string }): string[] => {
  let section1 = _t('Micro Lesson');
  if (videoData.videosInLesson) {
    section1 += _t(' (#{videosInLesson} videos)', { videosInLesson: videoData.videosInLesson });
  }
  const durationMs = moment.duration(videoData.duration).asMilliseconds();
  const section2 = getDurationFormatted(durationMs ?? 0);
  return [section1, section2];
};

export type EnterpriseCardData = {
  objectID: string;
  hitPosition: number;
  query?: string;
  queryID: string;
  listIndex: number;
  hasNoResults: boolean;
  filtersApplied: Record<string, string[]>;
  indexName?: string;
  indexPosition?: number;
};

type EnterpriseSearchCardProps = {
  id?: string;
  name?: string;
  slug?: string;
  imageUrl?: string;
  url?: string;
  productType?: string;
  productDifficultyLevel?: string;
  productDuration?: string;
  duration?: string;
  partners?: Array<{ name: string }>;
  partnerLogos?: string[];
  cobrandingEnabled?: boolean;
  skills?: string[];
  translatedSkills?: string[];
  avgProductRating?: number;
  numProductRatings?: number;
  isNewContent?: boolean;
  isPartOfCourseraPlus?: boolean;
  isCreditEligible?: boolean;
  isCourseFree?: boolean;
  isPathwayContent?: boolean;
  videosInLesson?: number;
  parentCourseName?: string;
  parentLessonName?: string;
  completions?: number;
  session?: string;
  adminContent?: ProductCardProps['adminContent'];
  hitPosition: number;
  variant?: ProductCardProps['variant'];
  urlToUse?: string;
  cardData: EnterpriseCardData;
  onSearchCardClick?: () => void;
};

export const EnterpriseSearchCard = ({
  id,
  name,
  slug,
  imageUrl,
  url,
  productType,
  productDifficultyLevel,
  productDuration,
  duration,
  partners,
  partnerLogos,
  cobrandingEnabled,
  skills,
  translatedSkills,
  avgProductRating,
  numProductRatings,
  isNewContent,
  isPartOfCourseraPlus,
  isCreditEligible,
  isCourseFree,
  isPathwayContent,
  videosInLesson,
  parentCourseName,
  parentLessonName,
  completions,
  session,
  adminContent,
  hitPosition,
  variant,
  urlToUse,
  cardData,
  onSearchCardClick,
}: EnterpriseSearchCardProps) => {
  const isSFC = isShortFormContent(productType ?? undefined);
  const entityType = LEARNING_PRODUCTS_ENUM_MAP[productType?.toUpperCase() || ''];

  // Transform product difficulty
  const translatedProductDifficultyLevel = getTranslatedDifficulty(
    productDifficultyLevel || ''
  ) as (typeof PRODUCT_DIFFICULTY_LEVELS)[keyof typeof PRODUCT_DIFFICULTY_LEVELS];

  // Transform partners data
  const partnersData = partners ? getPartnerNamesAndLogos(partners, !!cobrandingEnabled, partnerLogos) : undefined;

  const derivedSlug = slug || url?.substring(url.lastIndexOf('/') + 1);
  const trackingName = cardData.hasNoResults ? 'no_results_search_card' : 'search_card';

  return (
    <Grid key={id} item md={variant === 'list' ? 12 : 4} sm={variant === 'list' ? 12 : 6} xs={12} component="li">
      <div css={styles.targetSearchCardWrapper}>
        <LearningProductCard
          bodyContent={
            isSFC && parentCourseName ? (
              <CardClips clipInfo={{ courseName: parentCourseName, lessonName: parentLessonName }} />
            ) : null
          }
          ImageComponent={
            <LearningProductCardImage
              imageUrl={imageUrl || ''}
              enableFluidWidthForLazyImg={true}
              enableLazyLoading={hitPosition > LAZY_LOAD_THRESHOLD}
              imageParams={{
                ...getImagePropOverrides(partnersData ?? [], imageUrl ?? ''),
              }}
            />
          }
          preBadgeContent={
            <div>
              {session && !adminContent && <CardEnrollmentDate>{session}</CardEnrollmentDate>}
              {isSFC && <CardWatchNow watchNow={{ viewCount: Formatters.largeNumber(completions ?? 0) }} />}
            </div>
          }
          badgeConfig={{
            isPathwayContent: !isSFC && isPathwayContent,
            productType: entityType,
          }}
          linkProps={{
            href: urlToUse,
            target: urlToUse ? '_blank' : undefined,
            rel: urlToUse ? 'noopener noreferrer' : undefined,
            component: TrackedLink2,
          }}
          skills={!isSFC ? translatedSkills || skills : undefined}
          statusTags={getProductCardStatusTags({ isNew: false, statusTags: [], slug: derivedSlug })}
          partners={partnersData}
          name={name || ''}
          id={id}
          slug={derivedSlug}
          numProductRatings={numProductRatings}
          avgProductRating={avgProductRating}
          productType={entityType}
          productDifficultyLevel={translatedProductDifficultyLevel}
          productDuration={productDuration}
          variant={variant}
          cardIndex={hitPosition}
          indexName={cardData.indexName}
          adminContent={adminContent}
          customMetadata={isSFC && entityType ? getVideoMetadataFromHit({ videosInLesson, duration }) : undefined}
          trackingProps={{
            trackedLinkProps: {
              trackingName,
              withVisibilityTracking: true,
              requireFullyVisible: true,
              data: cardData,
            },
            additionalV3TrackingData: {
              searchedQuery: cardData.query || '',
            },
          }}
          customOnClick={onSearchCardClick}
        />
      </div>
    </Grid>
  );
};
